import { advancedFilterCondition } from './advancedFilterCondition';
import { dateFilterOptions } from './dateFilterOptions';
import { shipmentDocumentTypeEnum } from './shipmentDocumentTypeEnum';

const fulfillmentOrderStatus = {
	returned: 'returned',
	assigned: 'assigned',
	palletized: 'palletized',
	entered: 'entered',
	picking: 'picking',
	packaged: 'packaged',
	exitScan: 'exit%20scan',
	reassigned: 'reassigned',
	replaced: 'replaced',
	notReceived: 'not%20received',
	canceled: 'canceled',
	labelCreated: 'label%20created',
	shippedExternally: 'shipped%20externally',
};

const onAssignStatuses = [fulfillmentOrderStatus.entered];

const onHoldStatuses = [
	fulfillmentOrderStatus.entered,
	fulfillmentOrderStatus.assigned,
	fulfillmentOrderStatus.picking,
	fulfillmentOrderStatus.labelCreated,
	fulfillmentOrderStatus.packaged,
	fulfillmentOrderStatus.palletized,
];

const assignCarrierAccountStatuses = [
	fulfillmentOrderStatus.assigned,
	fulfillmentOrderStatus.picking,
	fulfillmentOrderStatus.labelCreated,
	fulfillmentOrderStatus.packaged,
	fulfillmentOrderStatus.palletized,
]

const onSplitFulfillmentOrderStatuses = [
	fulfillmentOrderStatus.picking,
	fulfillmentOrderStatus.assigned,
	fulfillmentOrderStatus.entered,
];

const onCancelStatuses = [
	fulfillmentOrderStatus.entered,
	fulfillmentOrderStatus.assigned,
	fulfillmentOrderStatus.picking,
	fulfillmentOrderStatus.labelCreated,
	fulfillmentOrderStatus.packaged,
	fulfillmentOrderStatus.palletized,
	fulfillmentOrderStatus.reassigned,
];

const onUncancelStatuses = [
	fulfillmentOrderStatus.canceled
];

const onCarrierServiceChangeStatuses = [fulfillmentOrderStatus.assigned, fulfillmentOrderStatus.picking];

const onUpdateBuyShippingStatuses = [
	fulfillmentOrderStatus.assigned,
	fulfillmentOrderStatus.picking,
	fulfillmentOrderStatus.labelCreated,
	fulfillmentOrderStatus.packaged,
	fulfillmentOrderStatus.palletized,
];

const onChangeShippingStatuses = [
	fulfillmentOrderStatus.assigned,
	fulfillmentOrderStatus.picking,
	fulfillmentOrderStatus.labelCreated,
	fulfillmentOrderStatus.packaged,
	fulfillmentOrderStatus.palletized,
];

const onPrintLabelStatuses = [
	fulfillmentOrderStatus.labelCreated,
	fulfillmentOrderStatus.packaged,
	fulfillmentOrderStatus.palletized,
	fulfillmentOrderStatus.exitScan,
	fulfillmentOrderStatus.assigned,
	fulfillmentOrderStatus.picking,
];

const onPrintBOLStatuses = [
	fulfillmentOrderStatus.labelCreated,
	fulfillmentOrderStatus.packaged,
	fulfillmentOrderStatus.palletized,
	fulfillmentOrderStatus.exitScan,
	fulfillmentOrderStatus.assigned,
	fulfillmentOrderStatus.picking,
]

const onUploadLabelStatuses = [
	fulfillmentOrderStatus.assigned,
	fulfillmentOrderStatus.picking,
	fulfillmentOrderStatus.labelCreated,
	fulfillmentOrderStatus.packaged,
	fulfillmentOrderStatus.palletized,
	fulfillmentOrderStatus.exitScan,
	fulfillmentOrderStatus.shippedExternally,
	fulfillmentOrderStatus.canceled,
];

const onPrintPackingSlipStatuses = [
	fulfillmentOrderStatus.entered,
	fulfillmentOrderStatus.assigned,
	fulfillmentOrderStatus.picking,
	fulfillmentOrderStatus.labelCreated,
	fulfillmentOrderStatus.packaged,
	fulfillmentOrderStatus.palletized,
	fulfillmentOrderStatus.exitScan,
	fulfillmentOrderStatus.shippedExternally,
	fulfillmentOrderStatus.canceled,
];

const onPullLabelStatuses = [fulfillmentOrderStatus.assigned, fulfillmentOrderStatus.picking];

// True when a grid row (FulfillmentChannelOrderREST) has a stored Priority1 LTL quote id, i.e. it won an
// LTL rate at assignment. Read off the FO Json blob ({"priority1":{"quoteId":...}}), mirroring the backend
// FulfillmentOrder.GetPriority1QuoteId().
const rowHasPriority1LtlQuote = priority1Data => {
	if (!priority1Data) return false;
	try {
		return !!(priority1Data && priority1Data.quoteId != null);
	} catch (e) {
		return false;
	}
};

// The Priority1 carrier name for a fulfillment order that won an LTL rate, read off the raw FO Json blob's
// rateMatchKey ("ServiceLevel|CarrierName", e.g. "STANDARD|XPO LOGISTICS"). Returns just the carrier-name
// portion (everything after the first "|"), or null when there is no Priority1 rate match key.
const getPriority1CarrierName = rateMatchKey => {
	if (!rateMatchKey) return null;
	try {
		const separatorIndex = rateMatchKey.indexOf('|');
		return separatorIndex >= 0 ? rateMatchKey.substring(separatorIndex + 1) : null;
	} catch (e) {
		return null;
	}
};

// True when a grid row is a designated LTL order. Mirrors the backend FulfillmentOrder.IsLTLOrder() and the
// notLtlOrder check in CellDataStore - unlike isLtlShipmentRow it ignores a won LTL rate.
const isLtlOrderRow = item => !!item && (item.orderChannel === 'LTL Freight' || item.requiredCarrierService === 'LTL');

// True when a grid row is an LTL shipment: a designated "LTL Freight" order, or one that won an LTL rate.
// The won-an-LTL-rate half comes straight off the backend IsLtlShipment flag the grid function projects
// (Json #>> '{priority1,quoteId}'), same as the detail page reads - the raw json blob is a string here, so
// reaching into it for quoteId never matched.
const isLtlShipmentRow = item => isLtlOrderRow(item) || (!!item && item.isLtlShipment === true);

const onUploadBOLStatuses = [
	fulfillmentOrderStatus.assigned,
	fulfillmentOrderStatus.picking,
	fulfillmentOrderStatus.labelCreated,
	fulfillmentOrderStatus.packaged,
	fulfillmentOrderStatus.palletized,
	fulfillmentOrderStatus.exitScan,
	fulfillmentOrderStatus.shippedExternally,
	fulfillmentOrderStatus.canceled,
];

// Which document types the unified Upload Document modal may offer for a grid row. Kept here so the grid
// icon and the modal props it opens can never disagree. The FO details page computes the same flags from
// its own richer model.
const getRowUploadDocumentAvailability = (item, hasLabel) => {
	const status = item.fulfillmentOrderStatusAbrv || (item.fulfillmentOrderStatus && item.fulfillmentOrderStatus.abrv);
	const isLtl = isLtlShipmentRow(item);

	const canUploadShippingLabel = !isLtl && !hasLabel && onUploadLabelStatuses.indexOf(status) !== -1;
	const canUploadBillOfLading = isLtl && item.billOfLadingSource == null && onUploadBOLStatuses.indexOf(status) !== -1;
	const canUploadDocument = status !== fulfillmentOrderStatus.canceled;

	return {
		canUploadShippingLabel,
		canUploadBillOfLading,
		canUploadDocument,
		// An order that won an LTL rate off a regular channel ships like any other shipment, so its BOL upload
		// records the same details a label upload would. Designated LTL Freight orders pull their BOL from the
		// channel and keep the document-only upload.
		billOfLadingRequiresShipmentDetails: isLtl && !isLtlOrderRow(item),
		canUploadAnyDocument: canUploadShippingLabel || canUploadBillOfLading || canUploadDocument,
	};
};

// Which additional documents the Print Additional Documents modal may offer for a grid row, in display order.
// Shared by the grid icon and the modal props so they can never disagree.
const getRowPrintableAdditionalDocuments = (item, hasPiiAccess) => {
	const status = item.fulfillmentOrderStatusAbrv || (item.fulfillmentOrderStatus && item.fulfillmentOrderStatus.abrv);
	const types = [];

	if (
		item.hasPackingSlip &&
		item.printAdditionalDocs &&
		hasPiiAccess &&
		onPrintPackingSlipStatuses.indexOf(status) !== -1
	) {
		types.push(shipmentDocumentTypeEnum.packingSlip);
	}
	if (item.hasUpcLabel) {
		types.push(shipmentDocumentTypeEnum.upcLabel);
	}
	if (item.hasGs1Label) {
		types.push(shipmentDocumentTypeEnum.gs1Label);
	}

	return types;
};

const onNeverShippedStatuses = [fulfillmentOrderStatus.exitScan];

const onProductionModeStatuses = [fulfillmentOrderStatus.assigned, fulfillmentOrderStatus.picking];

const onMarkAsShippedStatuses = [
	fulfillmentOrderStatus.entered,
	fulfillmentOrderStatus.assigned,
	fulfillmentOrderStatus.picking,
	fulfillmentOrderStatus.labelCreated,
	fulfillmentOrderStatus.packaged,
	fulfillmentOrderStatus.palletized,
];

const voidLabelStatuses = [
	fulfillmentOrderStatus.assigned,
	fulfillmentOrderStatus.picking,
	fulfillmentOrderStatus.labelCreated,
	fulfillmentOrderStatus.packaged,
	fulfillmentOrderStatus.palletized,
];

const onReoptimizeStatuses = [fulfillmentOrderStatus.assigned];

const initialOrderFilteringStatuses = [
	'Assigned',
	'Canceled',
	'Entered',
	'Label Created',
	'Not Received',
	'Packaged',
	'Palletized',
	'Picking',
	'Reassigned',
	'Replaced',
	'Returned',
	'Shipped Externally',
];

const initialAdvancedSearchFilterSetup = [
	{
		condition: 4,
		fieldName: 'fulfillmentOrderStatus.name',
		filterValue: [
			{ value: 'Assigned' },
			{ value: 'Canceled' },
			{ value: 'Entered' },
			{ value: 'Label Created' },
			{ value: 'Not Received' },
			{ value: 'Packaged' },
			{ value: 'Palletized' },
			{ value: 'Picking' },
			{ value: 'Reassigned' },
			{ value: 'Replaced' },
			{ value: 'Returned' },
			{ value: 'Shipped Externally' },
		],
	},
];

export {
	fulfillmentOrderStatus,
	onAssignStatuses,
	onHoldStatuses,
	assignCarrierAccountStatuses,
	onCancelStatuses,
	onUncancelStatuses,
	onCarrierServiceChangeStatuses,
	onUpdateBuyShippingStatuses,
	onChangeShippingStatuses,
	onPrintLabelStatuses,
	onPrintBOLStatuses,
	onPrintPackingSlipStatuses,
	onPullLabelStatuses,
	rowHasPriority1LtlQuote,
	getPriority1CarrierName,
	isLtlShipmentRow,
	isLtlOrderRow,
	getRowUploadDocumentAvailability,
	getRowPrintableAdditionalDocuments,
	onUploadBOLStatuses,
	onNeverShippedStatuses,
	onMarkAsShippedStatuses,
	onProductionModeStatuses,
	initialOrderFilteringStatuses,
	initialAdvancedSearchFilterSetup,
	voidLabelStatuses,
	onSplitFulfillmentOrderStatuses,
	onReoptimizeStatuses,
	onUploadLabelStatuses,
};
